<template>
  <v-dialog v-model="isOpen" max-width="480">
    <v-card data-cy="trial-status-modal">
      <v-card-title class="pa-4">
        {{ t("message.TrialStatus.title") }}
      </v-card-title>
      <v-card-text class="pa-4">
        <div class="mb-4">
          <div class="text-caption text-grey">
            {{ t("message.TrialStatus.schoolLabel") }}
          </div>
          <div class="text-body-1">{{ props.trial?.school_name || "-" }}</div>
        </div>
        <div class="mb-4">
          <div class="text-caption text-grey">
            {{ t("message.TrialStatus.currentStatus") }}
          </div>
          <div class="text-body-2">{{ props.trial?.status || "-" }}</div>
        </div>
        <v-select
          v-model="selectedStatus"
          :items="statusOptions"
          item-title="title"
          item-value="value"
          :label="t('message.TrialStatus.newStatus')"
          data-cy="trial-status-select"
        />
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-btn
          color="error"
          variant="tonal"
          :loading="saving"
          :disabled="props.trial?.status === 'rejected'"
          @click="quickReject"
          data-cy="trial-status-quick-reject-btn"
        >
          Reject
        </v-btn>
        <v-spacer />
        <v-btn
          variant="outlined"
          @click="close"
          :disabled="saving"
          data-cy="trial-status-cancel-btn"
        >
          {{ t("message.AccountSettings.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="save"
          :loading="saving"
          :disabled="!canSave"
          data-cy="trial-status-save-btn"
        >
          {{ t("message.AccountSettings.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAlertsStore } from "@/stores/alerts";
import useTrials, { type TrialMembership } from "@/stores/support/trials";

const props = defineProps<{
  modelValue: boolean;
  trial: TrialMembership | null;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const { t } = useI18n();
const alertsStore = useAlertsStore();
const trialsStore = useTrials();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const statusValues: TrialMembership["status"][] = [
  "requested",
  "pending",
  "fullfilled",
  "expired",
  "cancelled",
  "rejected",
  "converted",
];

const statusOptions = computed(() => {
  return statusValues.map((value) => ({
    value,
    title: t(`message.TrialStatus.statuses.${value}`),
  }));
});

const selectedStatus = ref<TrialMembership["status"] | "">("");
const saving = ref(false);

const canSave = computed(() => {
  return !!props.trial && !!selectedStatus.value && selectedStatus.value !== props.trial.status;
});

watch(
  () => props.trial,
  (trial) => {
    selectedStatus.value = trial?.status || "";
  },
  { immediate: true }
);

const close = () => {
  isOpen.value = false;
};

const quickReject = async () => {
  if (!props.trial) return;
  saving.value = true;
  try {
    const result = await trialsStore.updateTrialStatus(
      props.trial.trial_id,
      'rejected' as TrialMembership['status']
    );
    if (result.status) {
      alertsStore.addAlert('success', t('message.TrialStatus.updatedSuccess'), 5);
      close();
    } else {
      alertsStore.addAlert('error', result.message || t('message.TrialStatus.updateError'), 10);
    }
  } catch (error) {
    alertsStore.addAlert('error', t('message.TrialStatus.updateError'), 10);
  } finally {
    saving.value = false;
  }
};

const save = async () => {
  if (!props.trial || !selectedStatus.value) return;

  saving.value = true;
  try {
    const result = await trialsStore.updateTrialStatus(
      props.trial.trial_id,
      selectedStatus.value as TrialMembership["status"]
    );
    if (result.status) {
      alertsStore.addAlert("success", t("message.TrialStatus.updatedSuccess"), 5);
      close();
    } else {
      alertsStore.addAlert("error", result.message || t("message.TrialStatus.updateError"), 10);
    }
  } catch (error) {
    console.error("Error updating trial status:", error);
    alertsStore.addAlert("error", t("message.TrialStatus.updateError"), 10);
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped lang="scss">
.text-grey {
  color: rgba(0, 0, 0, 0.6);
}
</style>
